import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { useApiCliList, useBulkDeleteCli, useDeallocateCli } from '../api';
import { cliStatusLabel, type CliRow } from '../types';

export function ApiCliListPage() {
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());

  const query = useApiCliList();
  const rows = query.data ?? [];

  const deallocate = useDeallocateCli();
  const bulkDelete = useBulkDeleteCli();

  const [pendingDelete, setPendingDelete] = useState<CliRow | null>(null);
  const [pendingBulk, setPendingBulk] = useState<{ ids: string[]; clear: () => void } | null>(
    null,
  );

  const columns = useMemo<ColumnDef<CliRow>[]>(() => {
    const base: ColumnDef<CliRow>[] = [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cliId}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
    ];

    if (isSubOrSuper) {
      base.push({
        accessorKey: 'cliStatus',
        header: 'Status',
        size: 120,
        cell: ({ row }) => cliStatusLabel(row.original.cliStatus),
      });
    }

    base.push(
      { accessorKey: 'dialerName', header: 'Dialer', size: 160 },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 140 },
    );

    if (isSubOrSuper) {
      base.push({
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => setPendingDelete(row.original)}
            aria-label={`Deallocate ${row.original.cli}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ),
      });
    }

    return base;
  }, [isSubOrSuper]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deallocate.mutate(
      {
        cliId: pendingDelete.cliId,
        userId: Number(userId),
        allocated: isSubOrSuper ? 1 : 2,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Deallocated ${pendingDelete.cli}`);
          setPendingDelete(null);
        },
        onError: () => toast.error('Could not deallocate the CLI.'),
      },
    );
  };

  const confirmBulkDelete = () => {
    if (!pendingBulk) return;
    bulkDelete.mutate(pendingBulk.ids.map(Number), {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted ${pendingBulk.ids.length} CLIs`);
        pendingBulk.clear();
        setPendingBulk(null);
      },
      onError: () => toast.error('Bulk delete failed.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title="All API CLIs"
        description="Caller-line identities in the API pool."
        actions={
          isSubOrSuper && (
            <Button asChild>
              <Link to="/upload-api-cli">
                <Upload className="h-4 w-4" />
                Upload API CLI
              </Link>
            </Button>
          )
        }
      />

      <DataTable<CliRow, unknown>
        tableId="cli:api-list:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search API CLIs…"
        emptyTitle="No API CLIs found"
        emptyDescription="Upload an API CLI file to populate this list."
        getRowId={(row) => String(row.cliId)}
        exportFilename="api-cli.csv"
        initialColumnPinning={isSubOrSuper ? { right: ['actions'] } : undefined}
        pageSize={10}
        enableRowSelection={isSubOrSuper}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setPendingBulk({ ids, clear })}
            disabled={ids.length === 0 || bulkDelete.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Delete {ids.length}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            API CLI <span className="font-medium text-foreground">{pendingDelete?.cli}</span>{' '}
            will be deallocated.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deallocate.isPending}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(o) => !o && setPendingBulk(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">
              {pendingBulk?.ids.length ?? 0}
            </span>{' '}
            selected API CLIs will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={bulkDelete.isPending}
        onConfirm={confirmBulkDelete}
      />
    </FillPage>
  );
}
